import { useQuery } from '@tanstack/react-query';
import { AdminService, PipelineAttemptsService } from 'app/API';
import { cancelOnSignal } from 'app/API/core/CancelablePromise';
import { adminDashboardKeys } from 'app/queries/adminDashboard/adminDashboardKeys';
import type { TCall } from 'app/queries/types/TCall';

export const useAdminDashboardQueries = (call: TCall) => {
  const organizations = useQuery({
    queryKey: adminDashboardKeys.domain('organizations'),
    queryFn: ({ signal }) =>
      call(() => cancelOnSignal(AdminService.getAdminOrganizationCount(), signal), { dontRedirectOnError: true }),
  });
  const workspaces = useQuery({
    queryKey: adminDashboardKeys.domain('workspaces'),
    queryFn: ({ signal }) =>
      call(() => cancelOnSignal(AdminService.getAdminWorkspaceCount(), signal), { dontRedirectOnError: true }),
  });
  const users = useQuery({
    queryKey: adminDashboardKeys.domain('users'),
    queryFn: ({ signal }) =>
      call(() => cancelOnSignal(AdminService.getAdminUserStatistics(), signal), { dontRedirectOnError: true }),
  });
  const transactions = useQuery({
    queryKey: adminDashboardKeys.domain('transactions'),
    queryFn: ({ signal }) =>
      call(() => cancelOnSignal(AdminService.getAdminTransactionCount(), signal), { dontRedirectOnError: true }),
  });
  const queue = useQuery({
    queryKey: adminDashboardKeys.domain('queue'),
    queryFn: ({ signal }) =>
      call(() => cancelOnSignal(PipelineAttemptsService.getAdminTranscriptSubmitterQueueState(), signal), {
        dontRedirectOnError: true,
      }),
  });
  const errors = useQuery({
    queryKey: adminDashboardKeys.domain('errors'),
    queryFn: ({ signal }) =>
      call(() => cancelOnSignal(PipelineAttemptsService.getAdminUnclassifiedPipelineErrors(), signal), {
        dontRedirectOnError: true,
      }),
  });
  const queries = { organizations, workspaces, users, transactions, queue, errors };
  return {
    data: {
      organizationsCount: organizations.data?.count,
      workspacesCount: workspaces.data?.count,
      transactionsCount: transactions.data?.count,
      ...users.data,
      ...queue.data,
      classifiedPipelineErrors: errors.data,
    },
    isFetching: Object.values(queries).some((query) => query.isFetching),
    failedDomains: Object.entries(queries).filter(([, query]) => query.isError),
  };
};
